import React, { useState, useCallback, useMemo } from 'react';
import {
  createStyles,
  makeStyles,
  Theme,
  Grid,
} from '@material-ui/core';

import yup from '../../../utility/yup';
import { VideoMenu, DuplicateVideoMenu } from '../../../types/items';
import {
  Action,
  ReactHookFormModal,
  ReactFormInput,
  OrganizationSelect,
  UserPermissionSelect,
} from '../../../components';
import { CopyIcon } from '../../../components/icon/regular';
import { useActionDispatcher } from '../../../hooks/index';
import { duplicateVideoMenuRequest } from '../../../store/modules/video-menus/actions';
import { useFormContext } from 'react-hook-form';

const useStyles = makeStyles(({ palette }: Theme) => createStyles({
  icon: {
    color: palette.icons.default,
    fontSize: '1rem',
  },
}));

type Props = {
  original: VideoMenu;
};

const schema = yup.object().shape({
  title: yup.string().required().label('Title'),
  user_owner_id: yup.number().min(1, 'Owner is required').label('Owner'),
});

const DuplicateAction = ({ original }: Props) => {
  const classes = useStyles();
  const [openModal, setOpenModal] = useState(false);

  const duplicateProjectForm: DuplicateVideoMenu = {
    title: original.title,
    user_owner_id: original.user_id,
    organization_id: original.organization_id,
    video_menu_id: original.id,
  };

  const submitHandler = useActionDispatcher(duplicateVideoMenuRequest);
  const openModalHandler = useCallback(() => {
    setOpenModal(true);
  }, [setOpenModal]);

  const closeModalHandler = useCallback(() => {
    setOpenModal(false);
  }, [setOpenModal]);

  return (
    <>
      <Action
        title="Duplicate"
        icon={<CopyIcon className={classes.icon} />}
        onClick={openModalHandler}
      />
      {openModal && (
        <ReactHookFormModal
          formSettings={{
            initialValues: duplicateProjectForm,
            validationSchema: schema,
            onSubmit: submitHandler,
            submitTitle: 'Duplicate',
          }}
          open={openModal}
          onCloseHandler={closeModalHandler}
          title="Duplicate Video Menu"
        >
          <DuplicateForm
            organizationId={original.organization_id}
            userId={original.user_id}
          />
        </ReactHookFormModal>
      )}
    </>
  );
};


const DuplicateForm = ({ organizationId, userId }: { userId: number; organizationId: number }) => {

  const userFirstCallFilters = useMemo(() => [{ key: 'user_id', value: userId }], [userId]);
  const { watch } = useFormContext();
  const currentOrgId = watch('organization_id');

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <ReactFormInput name="title" label="Title" />
      </Grid>
      <Grid item xs={6}>
        <OrganizationSelect
          name="organization_id"
          label="Organization"
          formType="react"
          firstCallFilters={[{ key: 'organization_id', value: organizationId }]}
        />
      </Grid>
      <Grid item xs={6}>
        <UserPermissionSelect
          label="Owner"
          name="user_owner_id"
          disableClearable
          disabled={currentOrgId < 1}
          formType="react"
          firstCallFilters={userFirstCallFilters}
          filters={currentOrgId > 0 ? [{ key: 'organization_id', value: organizationId }] : []}
          dependentFieldName="organization_id"
          selectFirstOption
        />
      </Grid>
    </Grid>
  );
};

export default DuplicateAction;
